import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useProjectContext } from './ProjectContext';
import notesApi from '@/api/notes.api';

/**
 * Sections of the shared right-hand sidebar panel. The panel is the Notes
 * panel (NotesContext owns `isNotesPanelOpen`); Fragments is a second
 * section inside it.
 */

export interface Note {
  id: string;
  toolName: string;
  toolDescription: string;
  toolBody: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  isInjected?: boolean; // Client-side only
}

interface NotesContextType {
  notes: Note[];
  setNotes: (notes: Note[]) => void;
  isNotesPanelOpen: boolean;
  setIsNotesPanelOpen: (open: boolean) => void;
  isEditorModalOpen: boolean;
  setIsEditorModalOpen: (open: boolean) => void;
  currentEditingNote: Note | null;
  setCurrentEditingNote: (note: Note | null) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  isLoading: boolean;
  error: string | null;
  hasLoadedNotes: boolean;
  addNote: (note: Omit<Note, 'id' | 'createdAt' | 'updatedAt' | 'isInjected'>) => Promise<void>;
  updateNote: (id: string, updates: Partial<Note>) => Promise<void>;
  deleteNote: (id: string) => Promise<void>;
  toggleNoteInjection: (id: string) => void;
  filteredNotes: Note[];
  refetchNotes: (search?: string) => Promise<void>;
}

const NotesContext = createContext<NotesContextType | undefined>(undefined);

interface NotesProviderProps {
  children: ReactNode;
}

export const NotesProvider: React.FC<NotesProviderProps> = ({ children }) => {
  // Get current project ID from ProjectContext
  const { selectedProjectId } = useProjectContext();
  
  // State
  const [notes, setNotes] = useState<Note[]>([]);
  const [isNotesPanelOpen, setIsNotesPanelOpenState] = useState(false);
  
  const setIsNotesPanelOpen = (open: boolean) => {
    setIsNotesPanelOpenState(open);
  };
  const [isEditorModalOpen, setIsEditorModalOpen] = useState(false);
  const [currentEditingNote, setCurrentEditingNote] = useState<Note | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch notes from API with optional search
  const refetchNotes = async (search?: string) => {
    if (!selectedProjectId) {
      setNotes([]);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const fetchedNotes = await notesApi.fetchNotes(selectedProjectId, search);
      setNotes(fetchedNotes);
      setHasLoadedNotes(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch notes');
      console.error('Error fetching notes:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Track if notes have been loaded at least once
  const [hasLoadedNotes, setHasLoadedNotes] = useState(false);

  // Clear notes when project changes (don't auto-fetch)
  useEffect(() => {
    setNotes([]);
    setHasLoadedNotes(false);
    setSearchTerm(''); // Reset search when project changes
  }, [selectedProjectId]);

  // Debounced search effect (only if notes have been loaded)
  useEffect(() => {
    if (!hasLoadedNotes) return; // Don't search if notes haven't been loaded yet

    const timeoutId = setTimeout(() => {
      if (selectedProjectId) {
        refetchNotes(searchTerm);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedProjectId, hasLoadedNotes]);

  // API operations
  const addNote = async (noteData: Omit<Note, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!selectedProjectId) {
      throw new Error('No project selected');
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const newNote = await notesApi.createNote(selectedProjectId, noteData.toolName, noteData.toolDescription, noteData.toolBody, noteData.isActive);
      setNotes(prevNotes => [newNote, ...prevNotes]);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create note';
      
      // Don't set global error for validation errors (like duplicates) - those should only show in the modal
      if (!errorMessage.toLowerCase().includes('duplicate') && !errorMessage.toLowerCase().includes('already exists')) {
        setError(errorMessage);
      }
      
      throw err; // Re-throw the original error with specific message
    } finally {
      setIsLoading(false);
    }
  };

  const updateNote = async (id: string, updates: Partial<Note>) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const updatedNote = await notesApi.updateNote(id, updates.toolName, updates.toolDescription, updates.toolBody, updates.isActive);
      setNotes(prevNotes =>
        prevNotes.map(note => note.id === id ? updatedNote : note)
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update note';
      
      // Don't set global error for validation errors (like duplicates) - those should only show in the modal
      if (!errorMessage.toLowerCase().includes('duplicate') && !errorMessage.toLowerCase().includes('already exists')) {
        setError(errorMessage);
      }
      
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteNote = async (id: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      await notesApi.deleteNote(id);
      setNotes(prevNotes => prevNotes.filter(note => note.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete note');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Client-side injection toggle (not persisted to API)
  const toggleNoteInjection = (id: string) => {
    setNotes(prevNotes =>
      prevNotes.map(note =>
        note.id === id
          ? { ...note, isInjected: !note.isInjected }
          : note
      )
    );
  };


  // Notes are already filtered by backend search, so just return them
  const filteredNotes = notes;

  const value: NotesContextType = {
    notes,
    setNotes,
    isNotesPanelOpen,
    setIsNotesPanelOpen,
    isEditorModalOpen,
    setIsEditorModalOpen,
    currentEditingNote,
    setCurrentEditingNote,
    searchTerm,
    setSearchTerm,
    isLoading,
    error,
    hasLoadedNotes,
    addNote,
    updateNote,
    deleteNote,
    toggleNoteInjection,
    filteredNotes,
    refetchNotes,
  };

  return (
    <NotesContext.Provider value={value}>
      {children}
    </NotesContext.Provider>
  );
};

export const useNotes = (): NotesContextType => {
  const context = useContext(NotesContext);
  if (context === undefined) {
    throw new Error('useNotes must be used within a NotesProvider');
  }
  return context;
};